import React from 'react';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import OutlinedInput from '@material-ui/core/OutlinedInput';

import { Clear, NotInterested } from '@material-ui/icons';
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft';
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter';
import FormatAlignRightIcon from '@material-ui/icons/FormatAlignRight';
import { useTranslation } from 'react-i18next';

import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { DEFAULT_FONT, fontWeights, fonts } from '../../../../../../../../../constants/playerConfigurationsConstant';
import ColorPicker from '../../../../../../../../../reusable/ColorPicker/ColorPicker';
import { IconNames, getIconByName } from '../../../../../../../../../reusable/IconLibrary/IconLibrary';
import CustomSlider from '../../../../../../../../../reusable/Slider/CustomSlider';
import { getWidgetProps } from '../../../../../../../utils/helper';
import CardData from '../../../../../../../utils/CardData';
import { useCustomStyles } from "../../../../utils"
import TabBar from '../../../../../../../utils/TabBar';
import { WidgetProps } from '../../../../../../widgetOverlay/widgetInteractionItem/WidgetProps';

const ICON_PLACEMENT_OPTIONS = [
	{ key: 'left', value: 'left', label: 'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_ICON_PLACEMENT_LEFT' },
	{ key: 'top', value: 'top', label: 'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_ICON_PLACEMENT_TOP' },
	{
		key: 'right',
		value: 'right',
		label: 'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_ICON_PLACEMENT_RIGHT',
	},
	{
		key: 'bottom',
		value: 'bottom',
		label: 'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_ICON_PLACEMENT_BOTTOM',
	},
	{
		key: 'top-left',
		value: 'top-left',
		label: 'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_ICON_PLACEMENT_TOP_LEFT',
	},
	{
		key: 'top-right',
		value: 'top-right',
		label: 'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_ICON_PLACEMENT_TOP_RIGHT',
	},
	{
		key: 'bottom-left',
		value: 'bottom-left',
		label: 'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_ICON_PLACEMENT_BOTTOM_LEFT',
	},
	{
		key: 'bottom-right',
		value: 'bottom-right',
		label: 'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_ICON_PLACEMENT_BOTTOM_RIGHT',
	},
];

const SLIDER_OPTION_MARKS = [
	{ value: 0, label: '0px' },
	{ value: 20 },
	{ value: 40 },
	{ value: 60 },
	{ value: 80 },
	{ value: 100 },
	{ value: 120 },
	{ value: 140 },
	{ value: 160 },
	{ value: 180 },
	{ value: 200, label: '200px' },
];

// Same scale the Text widget uses for line height (0 -> 1.5).
const LINE_HEIGHT_MARKS = [
	{ value: 0, label: '0' },
	{ value: 15 },
	{ value: 30 },
	{ value: 45 },
	{ value: 60 },
	{ value: 75 },
	{ value: 90 },
	{ value: 105 },
	{ value: 120 },
	{ value: 135 },
	{ value: 150, label: '1.5' },
];

type ButtonContentTypeProps = {
	widgetTemplate: WidgetProps.WidgetTemplate;
	textHoverSetting: boolean;
	setTextHoverSetting: (_value: boolean) => {};
	handleTextLabelChange: () => {};
	handleTextLabelFontChange: () => {};
	handleTextLabelFontSizeChange: () => {};
	handleTextLabelFontWeightChange: () => {};
	handleTextLabelColorChange: (_event: {} | undefined, _color: {}) => {};
	handleTextLabelBGColorChange: (_event: {} | undefined, _color: {}) => {};
	handleTextLabelHoverFontWeightChange: () => {};
	handleTextLabelHoverColorChange: (_event: {} | undefined, _color: {}) => {};
	handleTextLabelHoverBGColorChange: (_event: {} | undefined, _color: {}) => {};
	handleUpdateIconToButton: (_iconKey: IconNames | '', _size?: string, _position?: string, _spacing?: string) => {};
	onShowIconLibraryPickerModal: () => {};

	handleBGColorChange: (_event: {} | undefined, _color: {}) => {};
	handleHoverBGColorChange: (_event: {} | undefined, _color: {}) => {};
	handleStylingAttributeChange: (_objKey: string | null, _attrKey: string, _value: any) => {};
};

const ButtonContent = ({
	widgetTemplate: widgetTemplateProp,
	textHoverSetting,
	setTextHoverSetting,
	handleTextLabelChange,
	handleTextLabelFontChange,
	handleTextLabelFontSizeChange,
	handleTextLabelFontWeightChange,
	handleTextLabelColorChange,
	handleTextLabelHoverFontWeightChange,
	handleTextLabelHoverColorChange,
	handleUpdateIconToButton,
	onShowIconLibraryPickerModal,
	handleStylingAttributeChange,
}: ButtonContentTypeProps) => {
	const widgetTemplate = getWidgetProps(widgetTemplateProp) as WidgetProps.WidgetTemplate;
	const {
		style: {
			textAlignment,
			textLabel: { text, font, fontSize, fontWeight, color, lineHeight, shadowColor, hoverFontWeight, hoverColor } = {},
			iconConfig: { size, position, spacing } = {}
		},
		settings: { iconKey } = {},
	} = widgetTemplate;

	const { t: translator } = useTranslation();
	const classes = useCustomStyles()

	const isShowIconOpt = Boolean(iconKey);

	const IconToShowOnToggle = getIconByName((iconKey || 'AddCircleOutline') as IconNames, {
		color: isShowIconOpt ? 'primary' : 'none',
	});

	const IconToShowOnPicker = getIconByName((iconKey || 'AddCircleOutline') as IconNames, {});

	return (
		<CardData title={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT')}>
			<div className="widget-props-wrapper">
				<div className="scene-widget__control">
					<TabBar
						size="big"
						items={[
							{
								label: translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_NORMAL'),
								id: 'normal',
								selected: !textHoverSetting,
							},
							{
								label: translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_HOVER'),
								id: 'hover',
								selected: textHoverSetting,
							},
						]}
						onItemClick={(_event: any, item: any) =>  setTextHoverSetting(item.id === 'hover')}
					/>
				</div>
				{!textHoverSetting && (
					<>
						<FormControlLabel
							className={'scenario-chart__control'}
							control={<OutlinedInput value={text} className={classes.input} />}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_TEXT_LABEL')}
							onChange={handleTextLabelChange}
							labelPlacement="start"
						/>

						<FormControlLabel
							className={'scenario-chart__control'}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_CONTENT_ALIGNMENT')}
							labelPlacement="start"
							control={
								<ToggleButtonGroup className="button-icon-configure-container">
									<ToggleButton
										value={'left'}
										aria-label="left aligned"
										size="small"
										className="toggle-button-custom"
										onClick={() => handleStylingAttributeChange(null, 'textAlignment', 'left')}
										selected={textAlignment === 'left'}
									>
										<FormatAlignLeftIcon />
									</ToggleButton>
									<ToggleButton
										value={'center'}
										aria-label="center aligned"
										size="small"
										className="toggle-button-custom"
										onClick={() => handleStylingAttributeChange(null, 'textAlignment', 'center')}
										selected={!textAlignment || textAlignment === 'center'}
									>
										<FormatAlignCenterIcon />
									</ToggleButton>
									<ToggleButton
										value={'right'}
										aria-label="right aligned"
										size="small"
										className="toggle-button-custom"
										onClick={() => handleStylingAttributeChange(null, 'textAlignment', 'right')}
										selected={textAlignment === 'right'}
									>
										<FormatAlignRightIcon />
									</ToggleButton>
								</ToggleButtonGroup>
							}
						/>

						<FormControlLabel
							className={'scenario-chart__control'}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_FONT_FAMILY')}
							onChange={handleTextLabelFontChange}
							labelPlacement="start"
							control={
								<Select variant="outlined" value={font} className={classes.input}>
									{fonts.map((fontOption) => (
										<MenuItem value={fontOption} key={fontOption}>
											{fontOption === DEFAULT_FONT ? translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_FONT_FAMILY_DEFAULT') : fontOption}
										</MenuItem>
									))}
								</Select>
							}
						/>

						<FormControlLabel
							className={'scenario-chart__control'}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_FONT_SIZE')}
							onChange={handleTextLabelFontSizeChange}
							labelPlacement="start"
							control={
								<Select variant="outlined" value={fontSize} className={classes.input}>
									{Array.from(new Array(68)).map((s, size) => (
										<MenuItem value={size + 5} key={size}>
											{size + 5}
										</MenuItem>
									))}
								</Select>
							}
						/>

						<FormControlLabel
							className={'scenario-chart__control'}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_FONT_WEIGHT')}
							onChange={handleTextLabelFontWeightChange}
							labelPlacement="start"
							control={
								<Select variant="outlined" value={fontWeight} className={classes.input}>
									{fontWeights.map((fontWeight) => (
										<MenuItem value={fontWeight} key={fontWeight}>
											{fontWeight}
										</MenuItem>
									))}
								</Select>
							}
						/>

						<FormControlLabel
							className={'scenario-chart__control'}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_STYLING_LINE_HEIGHT')}
							labelPlacement="start"
							control={
								<CustomSlider
									value={lineHeight}
									min={0}
									max={150}
									marks={LINE_HEIGHT_MARKS}
									onChange={(e: any, value: number | number[]) => {
										handleStylingAttributeChange('textLabel', 'lineHeight', value);
									}}
								/>
							}
						/>

						<FormControlLabel
							className={'scenario-chart__control'}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_TEXT_COLOR')}
							onChange={handleTextLabelColorChange}
							labelPlacement="start"
							control={
								<div className="scene-widgets__sort-input-control">
									<OutlinedInput value={color} className={classes.input} />
									<ColorPicker
										width="28px"
										height="28px"
										padding="4px"
										color={color}
										onChangeComplete={(color: any) => handleTextLabelColorChange(undefined, color)}
									/>
									<IconButton
										size="small"
										onClick={() => handleTextLabelColorChange(undefined, { hex: '' })}
									>
										<Clear />
									</IconButton>
								</div>
							}
						/>

						<FormControlLabel
							className={'scenario-chart__control'}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_STYLING_TEXT_SHADOW')}
							labelPlacement="start"
							control={
								<div className="scene-widgets__sort-input-control">
									<OutlinedInput value={shadowColor ?? ''} className={classes.input} />
									<ColorPicker
										width="28px"
										height="28px"
										padding="4px"
										color={shadowColor ?? ''}
										onChangeComplete={(color: any) =>
											handleStylingAttributeChange('textLabel', 'shadowColor', color.hex)
										}
									/>
									<IconButton
										size="small"
										onClick={() => handleStylingAttributeChange('textLabel', 'shadowColor', 'transparent')}
									>
										<Clear />
									</IconButton>
								</div>
							}
						/>
					</>
				)}
				{textHoverSetting && (
					<>
						<FormControlLabel
							className={'scenario-chart__control'}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_FONT_WEIGHT')}
							onChange={handleTextLabelHoverFontWeightChange}
							labelPlacement="start"
							control={
								<Select variant="outlined" value={hoverFontWeight ?? fontWeight} className={classes.input}>
									{fontWeights.map((fontWeight) => (
										<MenuItem value={fontWeight} key={fontWeight}>
											{fontWeight}
										</MenuItem>
									))}
								</Select>
							}
						/>

						<FormControlLabel
							className={'scenario-chart__control'}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_TEXT_COLOR')}
							onChange={handleTextLabelHoverColorChange}
							labelPlacement="start"
							control={
								<div className="scene-widgets__sort-input-control">
									<OutlinedInput value={hoverColor ?? color} className={classes.input} />
									<ColorPicker
										width="28px"
										height="28px"
										padding="4px"
										color={hoverColor ?? color}
										onChangeComplete={(color: any) => handleTextLabelHoverColorChange(undefined, color)}
									/>
									<IconButton
										size="small"
										onClick={() => handleTextLabelHoverColorChange(undefined, { hex: '' })}
									>
										<Clear />
									</IconButton>
								</div>
							}
						/>
					</>
				)}
				<Divider className="divider-extra-space" />
				<Box className="button-icon-configure-container">
					<Typography>
						{translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_BUTTON_ICON_SETTING')}
					</Typography>
					<ToggleButtonGroup>
						<ToggleButton
							value={'NoIcon'}
							aria-label="left aligned"
							size="small"
							className="toggle-button-custom"
							onClick={() => {
								handleUpdateIconToButton('', undefined, undefined, undefined);
							}}
							selected={!isShowIconOpt}
						>
							<NotInterested color={!isShowIconOpt ? 'primary' : 'disabled'} />
						</ToggleButton>
						<ToggleButton
							value={'WithIcon'}
							aria-label="right aligned"
							size="small"
							className="toggle-button-custom"
							onClick={() => {
								handleUpdateIconToButton(
									(iconKey || 'AddCircleOutline') as IconNames,
									size,
									position,
									spacing
								);
							}}
							selected={isShowIconOpt}
						>
							{IconToShowOnToggle}
						</ToggleButton>
					</ToggleButtonGroup>
				</Box>
				{isShowIconOpt && (
					<>
						<div className={'scene-widget__control'}>
							<div className="scene-widget__iconSelector--placeholder" onClick={onShowIconLibraryPickerModal}>
								{IconToShowOnPicker}
							</div>
						</div>
						<FormControlLabel
							className={'scenario-chart__control'}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_ICON_PLACEMENT_LABEL')}
							onChange={(e: any) => {
								const position = e.target.value;
								handleUpdateIconToButton(
									(iconKey || 'AddCircleOutline') as IconNames,
									size,
									position,
									spacing
								);
							}}
							labelPlacement="start"
							control={
								<Select variant="outlined" value={position} className={classes.input}>
									{ICON_PLACEMENT_OPTIONS.map(({ key, value, label }) => (
										<MenuItem value={value} key={key}>
											{translator(label) as string}
										</MenuItem>
									))}
								</Select>
							}
						/>
						<FormControlLabel
							className={'scenario-chart__control'}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_ICON_SIZE')}
							labelPlacement="start"
							control={
								<CustomSlider
									value={Number(size)}
									min={0}
									max={200}
									marks={SLIDER_OPTION_MARKS}
									onChange={(e: any, value: number | number[]) => {
										handleUpdateIconToButton(
											(iconKey || 'AddCircleOutline') as IconNames,
											String(value),
											position,
											spacing
										);
									}}
								/>
							}
						/>
						<FormControlLabel
							className={'scenario-chart__control'}
							label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_ICON_SPACING')}
							labelPlacement="start"
							control={
								<CustomSlider
									value={Number(spacing)}
									min={0}
									max={200}
									marks={SLIDER_OPTION_MARKS}
									onChange={(e: any, value: number | number[]) => {
										handleUpdateIconToButton(
											(iconKey || 'AddCircleOutline') as IconNames,
											size,
											position,
											String(value)
										);
									}}
								/>
							}
						/>
					</>
				)}
			</div>
		</CardData>
	);
};

export default ButtonContent;
